/**
 * AWAKENING RITUAL ONBOARDING — 4-step wizard
 *
 * Step 1: Business Profile  (enhanced from prior Onboarding.js — same fields + new ones)
 * Step 2: Competitor Intelligence Setup
 * Step 3: The Awakening (cinematic — 6 god cards)
 * Step 4: Command Center (summary + dashboard handoff)
 *
 * Spec: /app/memory/digest_audit_report.md prompt #4
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, ArrowRight, ArrowLeft, Globe, Building2,
  Shield, Eye, Lock, Check, Loader2, Zap, X, Plus,
  TrendingUp, Target, Mail, Users, Briefcase
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  'Marketing', 'Technology', 'E-Commerce', 'Business', 'Health',
  'SaaS / Software', 'Agency / Consulting', 'Finance / Fintech',
  'Education / EdTech', 'Real Estate', 'Media / Publishing', 'B2B Services',
];

const CONTENT_GOALS = [
  { id: 'seo', label: 'Drive organic traffic (SEO)' },
  { id: 'leads', label: 'Generate leads' },
  { id: 'thought_leadership', label: 'Build thought leadership' },
  { id: 'outperform', label: 'Outperform competitors' },
  { id: 'scale', label: 'Scale content production' },
  { id: 'social', label: 'Improve social media presence' },
];

const CONTENT_TYPES = [
  'Blog posts', 'Social media', 'Email campaigns', 'Ad copy', 'All of the above',
];

const TEAM_SIZES = ['Just me', '2-5', '6-20', '20+'];

const GODS = [
  {
    id: 'athena',
    name: 'Athena',
    title: 'Intelligence',
    avatar: '🦉',
    color: 'amber',
    statuses: [
      { phase: 0, text: 'Scanning competitors…' },
      { phase: 1, text: 'Analyzing content strategy…' },
      { phase: 2, text: (r) => `Found ${r?.gaps_count ?? 0} content gaps` },
    ],
  },
  {
    id: 'hermes',
    name: 'Hermes',
    title: 'Content',
    avatar: '✉️',
    color: 'cyan',
    statuses: [
      { phase: 0, text: "Studying your industry's content voice…" },
      { phase: 1, text: 'Analyzing top-performing content…' },
      { phase: 2, text: (r) => `${r?.topics?.length ?? 0} content angles ready to forge` },
    ],
  },
  {
    id: 'apollo',
    name: 'Apollo',
    title: 'Distribution',
    avatar: '☀️',
    color: 'orange',
    statuses: [
      { phase: 0, text: 'Checking competitor social presence…' },
      { phase: 1, text: 'Mapping optimal posting schedule…' },
      { phase: 2, text: () => 'Distribution strategy ready' },
    ],
  },
  {
    id: 'nike',
    name: 'Nike',
    title: 'Growth',
    avatar: '🏆',
    color: 'emerald',
    statuses: [
      { phase: 0, text: 'Scanning trending topics…' },
      { phase: 1, text: 'Scoring opportunities by competition level…' },
      { phase: 2, text: (r) => `${r?.count ?? 0} trending opportunities found` },
    ],
  },
  {
    id: 'plutus',
    name: 'Plutus',
    title: 'ROI',
    avatar: '💰',
    color: 'yellow',
    statuses: [
      { phase: 0, text: 'Estimating content value in your industry…' },
      { phase: 1, text: 'Calculating potential ROI…' },
      { phase: 2, text: (r) => `$${(r?.monthly_value_usd || 0).toLocaleString()}/mo projected` },
    ],
  },
  {
    id: 'war_room',
    name: 'War Room',
    title: 'Council',
    avatar: '⚔️',
    color: 'purple',
    statuses: [
      { phase: 0, text: 'Gods assembling…' },
      { phase: 1, text: 'Debating your competitive position…' },
      { phase: 2, text: () => 'First strategic briefing ready' },
    ],
  },
];

const colorClasses = {
  amber:   { bg: 'from-amber-500/20 to-orange-500/10',   border: 'border-amber-500/40',   icon: 'text-amber-400',   glow: 'shadow-amber-500/30' },
  cyan:    { bg: 'from-cyan-500/20 to-blue-500/10',      border: 'border-cyan-500/40',    icon: 'text-cyan-400',    glow: 'shadow-cyan-500/30' },
  orange:  { bg: 'from-orange-500/20 to-red-500/10',     border: 'border-orange-500/40',  icon: 'text-orange-400',  glow: 'shadow-orange-500/30' },
  emerald: { bg: 'from-emerald-500/20 to-green-500/10',  border: 'border-emerald-500/40', icon: 'text-emerald-400', glow: 'shadow-emerald-500/30' },
  yellow:  { bg: 'from-yellow-500/20 to-amber-500/10',   border: 'border-yellow-500/40',  icon: 'text-yellow-400',  glow: 'shadow-yellow-500/30' },
  purple:  { bg: 'from-purple-500/20 to-pink-500/10',    border: 'border-purple-500/40',  icon: 'text-purple-400',  glow: 'shadow-purple-500/30' },
};

// ─── Step 1: Business Profile ───
const Step1Profile = ({ data, setData, onNext, isReRun }) => {
  const can = data.businessName.trim().length >= 2 && data.industry.length > 0;
  const toggleGoal = (id) => {
    const has = data.contentGoals.includes(id);
    setData({ ...data, contentGoals: has ? data.contentGoals.filter(g => g !== id) : [...data.contentGoals, id] });
  };

  return (
    <div className="animate-fade-in" data-testid="awakening-step-1">
      <div className="text-center mb-6">
        <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-amber-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-1">Tell us about your business</h2>
        <p className="text-gray-500 text-xs">Step 1 of 4 · The gods need context to give you relevant strategy</p>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Business Name *</label>
          <input
            type="text" value={data.businessName}
            onChange={(e) => setData({ ...data, businessName: e.target.value })}
            placeholder="e.g. Acme Corp"
            className="w-full px-4 py-2.5 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm placeholder-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none"
            data-testid="awakening-business-name"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Website URL</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input
              type="url" value={data.businessUrl}
              onChange={(e) => setData({ ...data, businessUrl: e.target.value })}
              placeholder="https://yoursite.com"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm placeholder-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none"
              data-testid="awakening-business-url"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Industry *</label>
          <div className="grid grid-cols-2 gap-2">
            {INDUSTRIES.map((ind) => (
              <button
                key={ind} onClick={() => setData({ ...data, industry: ind })}
                className={`px-3 py-2 rounded-lg text-xs text-left transition-all border ${
                  data.industry === ind
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                    : 'bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-600'
                }`}
                data-testid={`awakening-industry-${ind.toLowerCase().replace(/[^a-z]/g, '')}`}
              >{ind}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">What does your business do?</label>
          <textarea
            value={data.businessDescription}
            onChange={(e) => setData({ ...data, businessDescription: e.target.value })}
            placeholder="1-2 sentences — used to give gods context"
            rows={2}
            className="w-full px-4 py-2.5 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm placeholder-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none resize-none"
            data-testid="awakening-business-desc"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-2">What are you trying to achieve? <span className="text-gray-600">(pick any)</span></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CONTENT_GOALS.map((g) => {
              const checked = data.contentGoals.includes(g.id);
              return (
                <button key={g.id} onClick={() => toggleGoal(g.id)}
                  className={`px-3 py-2 rounded-lg text-xs text-left flex items-start gap-2 border transition-all ${
                    checked ? 'bg-purple-500/20 border-purple-500/50 text-purple-200' : 'bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-600'
                  }`}
                  data-testid={`awakening-goal-${g.id}`}>
                  <div className={`w-4 h-4 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${checked ? 'bg-purple-500 border-purple-500' : 'border-gray-600'}`}>
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span>{g.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Primary content type</label>
            <select
              value={data.primaryContentType}
              onChange={(e) => setData({ ...data, primaryContentType: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none"
              data-testid="awakening-content-type"
            >
              <option value="">Pick one…</option>
              {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Team size</label>
            <select
              value={data.teamSize}
              onChange={(e) => setData({ ...data, teamSize: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none"
              data-testid="awakening-team-size"
            >
              <option value="">Pick one…</option>
              {TEAM_SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </div>

      <button
        onClick={onNext} disabled={!can}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium text-sm hover:from-purple-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="awakening-next-1"
      >
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Step 2: Competitors ───
const Step2Competitors = ({ data, setData, onBack, onNext }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');

  // Fetch suggestions whenever industry changes — including on initial mount when
  // re-run lands directly on Step 2 (industry already hydrated from /api/auth/me).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        // If local state has no industry, ask the backend to use the saved profile industry
        const param = data.industry ? `?industry=${encodeURIComponent(data.industry)}` : '';
        const { data: r } = await axios.get(
          `${API}/user/awakening/competitor-suggestions${param}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setSuggestions(r.suggestions || []);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [data.industry]);

  const setComp = (idx, key, val) => {
    const next = [...data.competitors];
    while (next.length <= idx) next.push({ url: '', description: '' });
    next[idx] = { ...next[idx], [key]: val };
    setData({ ...data, competitors: next });
  };

  const fillSuggestion = (url) => {
    const idx = data.competitors.findIndex(c => !c.url);
    if (idx === -1) return;
    setComp(idx, 'url', url);
  };

  const validUrl = (u) => /^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+/i.test(u || '');

  const handleNext = () => {
    const filled = data.competitors.filter(c => c.url && c.url.trim());
    if (filled.length === 0) { setError('Add at least 1 competitor URL.'); return; }
    if (!filled.every(c => validUrl(c.url))) { setError('Some URLs look invalid. Use a domain like example.com or https://example.com.'); return; }
    setError('');
    onNext();
  };

  return (
    <div className="animate-fade-in" data-testid="awakening-step-2">
      <div className="text-center mb-6">
        <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center">
          <Target className="w-6 h-6 text-amber-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-1">Who are your competitors?</h2>
        <p className="text-gray-500 text-xs leading-relaxed max-w-md mx-auto">
          Step 2 of 4 · Athena will study them while you sleep. Add 1-3 competitor websites — we'll analyze
          their content strategy, find gaps they've missed, and show you exactly where to strike.
        </p>
      </div>

      <div className="space-y-3 mb-5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-lg border border-gray-800 bg-gray-900/40 p-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-400">Competitor {i + 1} {i === 0 && <span className="text-red-400">*</span>}</label>
              {data.competitors[i]?.url && (
                <button onClick={() => setComp(i, 'url', '')} className="text-xs text-gray-500 hover:text-white" title="Clear">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <input
              type="text" value={data.competitors[i]?.url || ''}
              onChange={(e) => setComp(i, 'url', e.target.value)}
              placeholder={i === 0 ? 'https://competitor.com (required)' : 'https://competitor.com (optional)'}
              className="w-full px-3 py-2 rounded-md bg-gray-950 border border-gray-700 text-white text-sm placeholder-gray-600 focus:border-purple-500 outline-none mb-2"
              data-testid={`awakening-competitor-url-${i}`}
            />
            <input
              type="text" value={data.competitors[i]?.description || ''}
              onChange={(e) => setComp(i, 'description', e.target.value)}
              placeholder="What do they do? (optional, 1 sentence)"
              className="w-full px-3 py-2 rounded-md bg-gray-950 border border-gray-800 text-gray-300 text-xs placeholder-gray-600 focus:border-purple-500 outline-none"
              data-testid={`awakening-competitor-desc-${i}`}
            />
          </div>
        ))}
      </div>

      {suggestions.length > 0 && (
        <div className="mb-5 rounded-lg bg-gray-900/30 border border-gray-800 p-3" data-testid="awakening-suggestions">
          <p className="text-xs text-gray-400 mb-2">Not sure? Common competitors in <span className="text-purple-300">{data.industry}</span>:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s.url} onClick={() => fillSuggestion(s.url)}
                className="px-2.5 py-1 rounded-full bg-gray-800 hover:bg-gray-700 text-xs text-gray-300 hover:text-white border border-gray-700 transition-colors"
                data-testid={`awakening-suggest-${s.name.toLowerCase().replace(/[^a-z]/g, '')}`}
              >
                <Plus className="w-3 h-3 inline mr-1" />{s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-3 p-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-xs" data-testid="awakening-error">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={onBack}
          className="px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-1"
          data-testid="awakening-back-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={handleNext}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium text-sm hover:from-purple-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2"
          data-testid="awakening-next-2">
          Begin Awakening <Zap className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─── Step 3: The Awakening (cinematic) ───
const Step3Awakening = ({ data, onComplete, onSkip }) => {
  // Per-god phase state: 'dormant' | 'active' | 'complete' | 'partial'
  const [godStates, setGodStates] = useState(GODS.reduce((m, g) => ({ ...m, [g.id]: { phase: 'dormant', step: 0, result: null } }), {}));
  const [overallProgress, setOverallProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const cancelRef = useRef(false);
  const resultRef = useRef(null);

  // Sequence: pulse each god through 3 phases (0→1→2). Real backend call runs in parallel.
  // The animation is purely visual; we use the actual result to populate the final card values.
  useEffect(() => {
    cancelRef.current = false;
    let mounted = true;

    const update = (godId, patch) => {
      if (!mounted) return;
      setGodStates(prev => ({ ...prev, [godId]: { ...prev[godId], ...patch } }));
    };

    const animateGod = async (godId, durationMs, finalResult, hadError) => {
      if (cancelRef.current) return;
      update(godId, { phase: 'active', step: 0 });
      await new Promise(r => setTimeout(r, durationMs * 0.35));
      if (cancelRef.current) return;
      update(godId, { step: 1 });
      await new Promise(r => setTimeout(r, durationMs * 0.4));
      if (cancelRef.current) return;
      update(godId, {
        phase: hadError ? 'partial' : 'complete',
        step: 2,
        result: finalResult,
      });
    };

    const runRitual = async () => {
      // Fire the real backend call immediately (parallel to animations)
      const token = localStorage.getItem('token');
      const apiPromise = axios.post(`${API}/user/awakening/run`, {
        competitors: data.competitors.filter(c => c.url),
        industry: data.industry,
        business_name: data.businessName,
        business_description: data.businessDescription,
        content_goals: data.contentGoals,
        team_size: data.teamSize,
        primary_content_type: data.primaryContentType,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 })
        .then(r => r.data?.result || {})
        .catch((e) => {
          console.error('Awakening API failed:', e);
          return { __error: true };
        });

      // Animate Athena (10s)
      const athenaAnim = animateGod('athena', 10000, null, false);
      await new Promise(r => setTimeout(r, 10500));

      // Wait for the actual result to be ready (or proceed if still pending — we'll back-fill)
      let result = await Promise.race([
        apiPromise,
        new Promise(res => setTimeout(() => res(null), 50000))
      ]);
      resultRef.current = result;

      // If result is back, finalize Athena's card with real data
      if (result && !result.__error) {
        update('athena', { phase: result.athena?.success ? 'complete' : 'partial', step: 2, result: result.athena });
      } else {
        update('athena', { phase: 'partial', step: 2, result: { gaps_count: 0 } });
      }
      await athenaAnim;

      if (cancelRef.current) return;

      // Step B: Hermes / Apollo / Nike / Plutus run in parallel (animation 8s each, staggered)
      const stagger = 800;
      const parallelGods = [
        { id: 'hermes',  dur: 9000 },
        { id: 'apollo',  dur: 7000 },
        { id: 'nike',    dur: 9000 },
        { id: 'plutus',  dur: 6000 },
      ];
      const parallelPromises = parallelGods.map((g, i) =>
        new Promise(async (resolve) => {
          await new Promise(r => setTimeout(r, i * stagger));
          if (cancelRef.current) return resolve();
          // If we don't have the result yet, wait briefly
          if (!result) {
            result = await apiPromise;
            resultRef.current = result;
          }
          const r = (result && !result.__error) ? result[g.id] : null;
          await animateGod(g.id, g.dur, r, !r?.success);
          resolve();
        })
      );
      await Promise.all(parallelPromises);

      if (cancelRef.current) return;

      // Step C: War Room (10s)
      if (!result) result = await apiPromise;
      const wr = (result && !result.__error) ? result.war_room : null;
      await animateGod('war_room', 10000, wr, !wr?.success);

      if (!cancelRef.current) {
        // Ensure we have the API result (in case race timeout fired)
        if (!result || result.__error) {
          result = await apiPromise;
          resultRef.current = result;
        }
        setDone(true);
        // Pass result up so Step 4 can render the summary
        setTimeout(() => onComplete(result || {}), 600);
      }
    };

    // Compute overall progress from godStates
    runRitual();
    return () => { mounted = false; cancelRef.current = true; };
    // eslint-disable-next-line
  }, []);

  // Recompute overall progress whenever godStates updates
  useEffect(() => {
    const total = GODS.length;
    const score = GODS.reduce((acc, g) => {
      const s = godStates[g.id];
      if (!s) return acc;
      if (s.phase === 'complete' || s.phase === 'partial') return acc + 1;
      if (s.phase === 'active') return acc + (s.step + 1) / 4;
      return acc;
    }, 0);
    setOverallProgress(Math.round((score / total) * 100));
  }, [godStates]);

  const handleSkip = () => {
    if (skipping) return;
    setSkipping(true);
    cancelRef.current = true;
    // Use whatever result we already have
    onSkip(resultRef.current || {});
  };

  return (
    <div className="fixed inset-0 bg-[#020617] flex flex-col items-center justify-center px-4 py-8 z-50 overflow-y-auto" data-testid="awakening-step-3">
      {/* Background atmospherics */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-32 w-[700px] h-[700px] bg-purple-600/8 rounded-full blur-[200px] animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-0 -right-32 w-[700px] h-[700px] bg-cyan-600/8 rounded-full blur-[200px] animate-pulse" style={{ animationDuration: '6s' }} />
      </div>

      <div className="relative z-10 w-full max-w-5xl">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 mb-3">
            <Zap className="w-4 h-4 text-purple-400 animate-pulse" />
            <span className="text-xs font-bold text-purple-300 tracking-widest uppercase">Step 3 of 4</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-white mb-2 tracking-tight" data-testid="awakening-h1">
            ⚡ Awakening Your AI Council
          </h1>
          <p className="text-sm sm:text-base text-gray-400">
            Your gods are analyzing your competitive landscape. This takes about 60–90 seconds.
          </p>
        </div>

        {/* God grid — 2x3 desktop, 1col mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {GODS.map((g) => {
            const s = godStates[g.id] || { phase: 'dormant', step: 0 };
            const c = colorClasses[g.color];
            const statusObj = g.statuses[s.step] || g.statuses[0];
            const statusText = typeof statusObj.text === 'function' ? statusObj.text(s.result) : statusObj.text;
            const phaseClass =
              s.phase === 'dormant' ? 'opacity-40 grayscale' :
              s.phase === 'active'  ? `bg-gradient-to-br ${c.bg} ${c.border} shadow-lg ${c.glow} scale-100` :
              s.phase === 'complete' ? `bg-gradient-to-br ${c.bg} border-emerald-500/40 shadow-md shadow-emerald-500/20` :
              s.phase === 'partial' ? `bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border-yellow-500/40` :
              'opacity-40';
            return (
              <div key={g.id}
                className={`rounded-xl border-2 p-5 transition-all duration-700 ${phaseClass}`}
                data-testid={`awakening-god-${g.id}`}
                data-phase={s.phase}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`text-3xl transition-all ${s.phase === 'active' ? 'animate-pulse' : ''}`}>
                    {g.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs uppercase tracking-widest font-bold ${c.icon}`}>{g.title}</div>
                    <div className="text-lg font-black text-white">{g.name}</div>
                  </div>
                  {s.phase === 'complete' && (
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center" data-testid={`awakening-check-${g.id}`}>
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                  )}
                  {s.phase === 'partial' && (
                    <div className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center text-yellow-400 font-bold text-xs" title="Partial data — will complete on next scan">
                      !
                    </div>
                  )}
                  {s.phase === 'active' && (
                    <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                  )}
                </div>
                <div className="text-xs text-gray-300 min-h-[2.5em] leading-snug" data-testid={`awakening-status-${g.id}`}>
                  {s.phase === 'dormant' ? 'Awaiting awakening…' :
                   s.phase === 'partial' ? 'Partial data — will complete on next scan' :
                   statusText}
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4 mb-4">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="text-gray-400">Overall progress</span>
            <span className="text-purple-300 font-bold" data-testid="awakening-progress">{overallProgress}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-gray-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 via-cyan-500 to-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        <div className="text-center">
          {!done && (
            <button onClick={handleSkip}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              data-testid="awakening-skip"
            >
              Skip and explore on your own →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Step 4: Command Center ───
const Step4CommandCenter = ({ data, result, onEnter }) => {
  const athena = result?.athena || {};
  const hermes = result?.hermes || {};
  const warRoom = result?.war_room || {};
  const nike = result?.nike || {};

  return (
    <div className="animate-fade-in" data-testid="awakening-step-4">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-3">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-bold text-emerald-300 tracking-widest uppercase">Step 4 of 4 · Awake</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">⚡ Your AI Council is Awake</h1>
        <p className="text-sm text-gray-400">Here's what your gods found in their first 60 seconds.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Card 1 — Competitive Intelligence */}
        <div className="rounded-xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-5" data-testid="cc-card-intelligence">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🦉</span>
            <div className="text-xs uppercase tracking-widest font-bold text-amber-400">Competitive Intelligence</div>
          </div>
          <div className="space-y-2 mb-4 text-sm text-gray-300">
            <div>Athena analyzed <strong className="text-white">{athena.competitors_scanned || 0}</strong> competitor{(athena.competitors_scanned || 0) === 1 ? '' : 's'}</div>
            <div>Found <strong className="text-white">{athena.gaps_count || 0}</strong> content gap{(athena.gaps_count || 0) === 1 ? '' : 's'}</div>
            {athena.top_opportunity && (
              <div className="pt-2 border-t border-amber-500/20">
                <div className="text-[10px] uppercase tracking-widest text-amber-400/70 mb-1">Top opportunity</div>
                <div className="text-xs text-gray-200 font-semibold leading-snug">{athena.top_opportunity}</div>
              </div>
            )}
          </div>
          <button onClick={() => onEnter('competitor-analyzer')}
            className="w-full py-2.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 text-xs font-bold transition-all"
            data-testid="cc-cta-intelligence">
            View Full Report →
          </button>
        </div>

        {/* Card 2 — Content Opportunities */}
        <div className="rounded-xl border-2 border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 p-5" data-testid="cc-card-content">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">✉️</span>
            <div className="text-xs uppercase tracking-widest font-bold text-cyan-400">Content Opportunities</div>
          </div>
          <div className="space-y-2 mb-4 text-sm text-gray-300">
            <div>Hermes identified <strong className="text-white">{hermes.topics?.length || 0}</strong> content angles</div>
            {hermes.topics?.slice(0, 3).map((t, i) => (
              <div key={i} className="text-xs text-gray-200 leading-snug truncate">• {t.title}</div>
            ))}
            <div className="pt-2 border-t border-cyan-500/20 text-xs text-cyan-300">
              <strong className="text-white">{nike.count || 0}</strong> trending topics in {data.industry}
            </div>
          </div>
          <button onClick={() => onEnter('generate', hermes.topics?.[0])}
            className="w-full py-2.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-200 text-xs font-bold transition-all"
            data-testid="cc-cta-content">
            Start Forging →
          </button>
        </div>

        {/* Card 3 — Strategic Briefing */}
        <div className="rounded-xl border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-pink-500/5 p-5" data-testid="cc-card-briefing">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">⚔️</span>
            <div className="text-xs uppercase tracking-widest font-bold text-purple-400">Strategic Briefing</div>
          </div>
          <div className="space-y-2 mb-4 text-sm text-gray-300">
            <div>Your War Room council debated your position</div>
            {warRoom.verdict_preview && (
              <div className="pt-2 border-t border-purple-500/20">
                <div className="text-xs italic text-gray-200 leading-snug line-clamp-3">"{warRoom.verdict_preview}"</div>
              </div>
            )}
            {!warRoom.verdict_preview && (
              <div className="pt-2 border-t border-purple-500/20">
                <div className="text-xs italic text-gray-500">Briefing will be ready after first scan completes.</div>
              </div>
            )}
          </div>
          <button onClick={() => onEnter('living-war-room')}
            className="w-full py-2.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-200 text-xs font-bold transition-all"
            data-testid="cc-cta-briefing">
            Read the Briefing →
          </button>
        </div>
      </div>

      <div className="text-center text-xs text-gray-500 mb-4 italic">
        Your gods will continue learning in the background. Check back tomorrow for your first Morning Briefing.
      </div>

      <button onClick={() => onEnter()}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 text-white font-bold text-base hover:from-purple-500 hover:via-pink-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
        data-testid="cc-enter-dashboard">
        Enter Your Dashboard <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
};

// ─── Step 1.5: Privacy ───
const StepPrivacy = ({ data, setData, onBack, onSubmit, error, submitting }) => (
  <div className="animate-fade-in" data-testid="awakening-step-privacy">
    <div className="text-center mb-6">
      <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center">
        <Shield className="w-6 h-6 text-green-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-1">Your Data, Your Rules</h2>
      <p className="text-gray-500 text-xs">Choose how your data is used. Change anytime in settings.</p>
    </div>

    <div className="space-y-3 mb-6">
      {[
        { id: 'godData', icon: Eye, color: 'purple', title: 'Personalized God Intelligence', desc: 'Allow the gods to analyze your business data to give you personalized strategy.', recommended: true },
        { id: 'publicSharing', icon: Globe, color: 'blue', title: 'Public Debate Sharing', desc: 'Allow your shared debates to include business context when viewed publicly.', recommended: false },
        { id: 'platformImprovement', icon: Lock, color: 'green', title: 'Anonymized Platform Improvement', desc: 'Allow anonymized usage patterns (never your business data) to help us improve.', recommended: false },
      ].map((opt) => {
        const checked = data.privacy[opt.id];
        return (
          <div key={opt.id}
            onClick={() => setData({ ...data, privacy: { ...data.privacy, [opt.id]: !checked } })}
            className={`p-4 rounded-xl border cursor-pointer transition-all ${checked ? 'bg-purple-500/10 border-purple-500/30' : 'bg-gray-900/50 border-gray-800'}`}
            data-testid={`awakening-privacy-${opt.id}`}>
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${checked ? 'bg-purple-500 border-purple-500' : 'border-gray-600'}`}>
                {checked && <Check className="w-3 h-3 text-white" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <opt.icon className={`w-3.5 h-3.5 text-${opt.color}-400`} />
                  <p className="text-sm font-medium text-white">{opt.title}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                {opt.recommended && <p className="text-[10px] text-purple-400/60 mt-1">Recommended</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs">{error}</div>}

    <div className="flex gap-3">
      <button onClick={onBack}
        className="px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-gray-400 text-sm hover:text-white flex items-center gap-1"
        data-testid="awakening-back-privacy">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <button onClick={onSubmit} disabled={submitting}
        className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium text-sm hover:from-purple-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="awakening-save-profile">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Continue to Competitors <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  </div>
);

// ─── Main Wizard ───
const AwakeningRitual = ({ onComplete, isReRun = false }) => {
  // Step values: 1=Profile, 1.5=Privacy, 2=Competitors, 3=Awakening, 4=Command Center
  const [step, setStep] = useState(isReRun ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [data, setData] = useState({
    businessName: '', businessUrl: '', industry: '', businessDescription: '',
    contentGoals: [], primaryContentType: '', teamSize: '',
    competitors: [{ url: '', description: '' }, { url: '', description: '' }, { url: '', description: '' }],
    privacy: { godData: true, publicSharing: false, platformImprovement: true },
  });

  // On mount, hydrate from existing user profile (especially for re-run)
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data: prof } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (prof) {
          setData(d => ({
            ...d,
            businessName: prof.business_name || d.businessName,
            businessUrl: prof.business_url || d.businessUrl,
            industry: prof.industry || d.industry,
            businessDescription: prof.business_description || d.businessDescription,
            contentGoals: Array.isArray(prof.content_goals) ? prof.content_goals : d.contentGoals,
            primaryContentType: prof.primary_content_type || d.primaryContentType,
            teamSize: prof.team_size || d.teamSize,
            competitors: Array.isArray(prof.competitors) && prof.competitors.length
              ? [0, 1, 2].map(i => prof.competitors[i] || { url: '', description: '' })
              : d.competitors,
          }));
        }
      } catch { /* noop */ }
    })();
  }, []);

  const saveProfile = async () => {
    setSubmitting(true); setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/user/onboarding`, {
        business_name: data.businessName.trim(),
        business_url: data.businessUrl.trim(),
        industry: data.industry,
        business_description: data.businessDescription.trim(),
        privacy_god_data: data.privacy.godData,
        privacy_public_sharing: data.privacy.publicSharing,
        privacy_platform_improvement: data.privacy.platformImprovement,
        content_goals: data.contentGoals,
        primary_content_type: data.primaryContentType,
        team_size: data.teamSize,
        competitors: data.competitors.filter(c => c.url),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitting(false);
      setStep(2);
    } catch (e) {
      setSubmitting(false);
      setError('Could not save your profile. Please try again.');
    }
  };

  const finalizeRitual = async (apiResult) => {
    setResult(apiResult || {});
    // Persist competitors update before showing Step 4
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/user/onboarding`, {
        business_name: data.businessName.trim(),
        business_url: data.businessUrl.trim(),
        industry: data.industry,
        business_description: data.businessDescription.trim(),
        privacy_god_data: data.privacy.godData,
        privacy_public_sharing: data.privacy.publicSharing,
        privacy_platform_improvement: data.privacy.platformImprovement,
        content_goals: data.contentGoals,
        primary_content_type: data.primaryContentType,
        team_size: data.teamSize,
        competitors: data.competitors.filter(c => c.url),
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* noop */ }
    setStep(4);
  };

  const enterDashboard = (target, payload) => {
    onComplete({ target, payload });
  };

  // Renders
  if (step === 3) {
    return <Step3Awakening data={data} onComplete={finalizeRitual} onSkip={finalizeRitual} />;
  }
  if (step === 4) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4 py-12">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-32 w-[700px] h-[700px] bg-purple-600/8 rounded-full blur-[200px]" />
          <div className="absolute bottom-0 -right-32 w-[700px] h-[700px] bg-cyan-600/8 rounded-full blur-[200px]" />
        </div>
        <div className="relative z-10 w-full max-w-4xl">
          <Step4CommandCenter data={data} result={result} onEnter={enterDashboard} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[180px]" />
        <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] bg-cyan-600/8 rounded-full blur-[180px]" />
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {/* Progress bar — 4 steps */}
        <div className="flex items-center gap-1 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 h-1 rounded-full overflow-hidden bg-gray-800">
              <div className={`h-full rounded-full transition-all duration-500 ${
                step >= s ? 'bg-gradient-to-r from-purple-500 to-cyan-500 w-full' : 'w-0'
              }`} />
            </div>
          ))}
        </div>

        {step === 1 && <Step1Profile data={data} setData={setData} onNext={() => setStep(1.5)} isReRun={isReRun} />}
        {step === 1.5 && <StepPrivacy data={data} setData={setData} onBack={() => setStep(1)} onSubmit={saveProfile} error={error} submitting={submitting} />}
        {step === 2 && <Step2Competitors data={data} setData={setData} onBack={() => setStep(isReRun ? 2 : 1.5)} onNext={() => setStep(3)} />}
      </div>
    </div>
  );
};

export default AwakeningRitual;
